import React, { useCallback, useEffect, useState } from 'react';
import {
  addOrganisationSystem,
  createOrganisationSystemLicense,
  extendLicense,
  getCustomerHistory,
  getApplications,
  markLicenseInactive,
  updateLicenseStatus,
  unbindDevice,
} from '../Services/api';
import AddSystemModal from '../Components/AddSystemModal';
import EventTimeline from '../Components/EventTimeline';
import ExtendLicenseModal from '../Components/ExtendLicenseModal';
import { formatLicenseDeviceInfo, formatLicenseExpiry, isLicenseExpired } from '../utils/deviceInfo';
import './CustomerDetailPage.css';

const CustomerDetailPage = ({ customerId, customerType, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [showAddSystem, setShowAddSystem] = useState(false);
  const [renewingSystem, setRenewingSystem] = useState(null);
  const [applicationData, setApplicationData] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await getCustomerHistory(customerId, customerType);
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to load customer');
    } finally {
      setLoading(false);
    }
  }, [customerId, customerType]);

  useEffect(() => {
    loadData();
    getApplications().then(setApplicationData).catch(() => {});
  }, [loadData]);

  const handleAddSystem = async ({ systemName, expiryMonths, offlineGraceHours, applicationId }) => {
    const system = await addOrganisationSystem(customerId, { systemName });
    await createOrganisationSystemLicense(customerId, system.id, {
      expiryMonths,
      offlineGraceHours,
      applicationId,
    });
    setShowAddSystem(false);
    await loadData();
  };

  const handleDeactivateSystem = async (licenseId) => {
    if (!window.confirm('Deactivate this system license?')) return;
    await markLicenseInactive(licenseId);
    await loadData();
  };

  const handleReactivateSystem = async (licenseId) => {
    await updateLicenseStatus(licenseId, 'active');
    await loadData();
  };

  const handleTransferSystem = async (licenseId) => {
    if (!window.confirm('Unbind this license from its current device? It can then be activated on a new machine.')) return;
    await unbindDevice(licenseId);
    await loadData();
  };

  const handleRenewSystem = async (licenseId, days) => {
    await extendLicense(licenseId, days);
    setRenewingSystem(null);
    await loadData();
  };

  const title =
    customerType === 'organisation'
      ? data?.customer?.org_name || 'Organisation'
      : data?.customer?.client_name || 'Customer';

  return (
    <div className="customer-detail-overlay" onClick={onClose}>
      <div className="customer-detail-panel" onClick={(e) => e.stopPropagation()}>
        <div className="customer-detail-header">
          <div>
            <h2>{title}</h2>
            <p className="customer-detail-subtitle">
              {customerType === 'organisation' ? 'Organisation customer' : 'Individual customer'}
            </p>
          </div>
          <button type="button" className="customer-detail-close" onClick={onClose}>
            ×
          </button>
        </div>

        {loading && <div className="customer-detail-loading">Loading…</div>}
        {error && <div className="customer-detail-error">{error}</div>}

        {!loading && !error && data && (
          <div className="customer-detail-body">
            {customerType === 'individual' ? (
              <div className="customer-info-grid">
                <div><strong>Client</strong><span>{data.customer.client_name}</span></div>
                <div><strong>Clinic</strong><span>{data.customer.clinic_name || '-'}</span></div>
                <div><strong>Phone</strong><span>{data.customer.phone || '-'}</span></div>
                <div><strong>City</strong><span>{data.customer.city || '-'}</span></div>
                <div><strong>License key</strong><code>{data.customer.license_key}</code></div>
                <div><strong>Status</strong><span>{data.customer.status}</span></div>
                <div><strong>Expiry</strong><span>{formatLicenseExpiry(data.customer)}</span></div>
                <div><strong>Device</strong><span>{formatLicenseDeviceInfo(data.customer)}</span></div>
              </div>
            ) : (
              <>
                <div className="customer-info-grid">
                  <div><strong>Contact</strong><span>{data.customer.contact_person || '-'}</span></div>
                  <div><strong>Phone</strong><span>{data.customer.phone || '-'}</span></div>
                  <div><strong>City</strong><span>{data.customer.city || '-'}</span></div>
                  <div><strong>Email</strong><span>{data.customer.email || '-'}</span></div>
                  <div><strong>Created</strong><span>{new Date(data.customer.created_at).toLocaleDateString()}</span></div>
                </div>

                <div className="customer-systems-header">
                  <h3 className="customer-systems-title">Systems</h3>
                  <button
                    type="button"
                    className="add-system-btn"
                    onClick={() => setShowAddSystem(true)}
                  >
                    Add System
                  </button>
                </div>
                <div className="customer-systems-list">
                  {(data.systems || []).map((system) => {
                    const expired = isLicenseExpired(system);
                    const statusText = system.device_id
                      ? expired
                        ? 'expired'
                        : system.status || 'active'
                      : 'Not yet activated';

                    return (
                      <details key={system.id} className="customer-system-card">
                        <summary>
                          <span className="system-name">{system.system_name}</span>
                          <span className={`system-status system-status-${expired ? 'expired' : system.status || 'none'}`}>
                            {statusText}
                          </span>
                          <span className="system-expiry">{formatLicenseExpiry(system)}</span>
                        </summary>
                        <div className="system-details">
                          <div><strong>License key</strong> <code>{system.license_key || '-'}</code></div>
                          <div><strong>Device</strong> {system.device_id ? formatLicenseDeviceInfo(system) : 'Not bound'}</div>
                          {system.license_id && (
                            <div className="system-actions">
                              {expired && (
                                <button type="button" className="system-renew-btn" onClick={() => setRenewingSystem(system)}>
                                  Extend
                                </button>
                              )}
                              {system.status === 'active' && (
                                <button type="button" className="system-deactivate-btn" onClick={() => handleDeactivateSystem(system.license_id)}>
                                  Deactivate
                                </button>
                              )}
                              {system.status === 'inactive' && (
                                <button type="button" className="system-reactivate-btn" onClick={() => handleReactivateSystem(system.license_id)}>
                                  Activate
                                </button>
                              )}
                              {system.device_id && (
                                <button type="button" className="system-transfer-btn" onClick={() => handleTransferSystem(system.license_id)}>
                                  Transfer to new machine
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </details>
                    );
                  })}
                </div>
              </>
            )}

            <h3 className="customer-timeline-title">Event timeline</h3>
            <EventTimeline events={data.timeline || []} />
          </div>
        )}
      </div>

      {showAddSystem && (
        <AddSystemModal
          onSubmit={handleAddSystem}
          onCancel={() => setShowAddSystem(false)}
          applicationData={applicationData}
        />
      )}
      {renewingSystem && (
        <ExtendLicenseModal
          license={renewingSystem}
          onExtend={(days) => handleRenewSystem(renewingSystem.license_id, days)}
          onCancel={() => setRenewingSystem(null)}
          showAlert={(message) => setError(message)}
        />
      )}
    </div>
  );
};

export default CustomerDetailPage;
